// src/contexts/ChannelContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import toastr          from 'toastr';

import {
  getBotStatus,
  startBot as apiStartBot,
  stopBot as apiStopBot,
  getChatStatus,
  startChat,
  stopChat,
  getMsgLists,
  addMsgList,
  deleteMsgList,
} from '../api/platform/contexts/api';

export const ChannelContext = createContext(null);

export const ChannelProvider = ({ children, platform }) => {
  /* ─── id-шники ─── */
  const [groupId,   setGroupId]   = useState(null);
  const [channelId, setChannelId] = useState(null);

  /* ─── прогресс / метрики ─── */
  const [tokensChecked, setTokensChecked] = useState(0);
  const [totalTokens,  setTotalTokens]    = useState(0);
  const [progress,     setProgress]       = useState(0);

  /* fallback (kick-специфика) */
  const [fallbackActive, setFallbackActive] = useState(0);
  const [fallbackTotal,  setFallbackTotal]  = useState(0);

  /* состояния */
  const [chatRunning, setChatRunning] = useState(false);
  const [botRunning,  setBotRunning]  = useState(false);

  /* настройки */
  const [intervalValue, setIntervalValue] = useState(5);
  const [accountMode,   setAccountMode]   = useState('random');

  /* списки сообщений */
  const [messageLists, setMessageLists] = useState([]);

  /* ——— helpers ——— */
  const location      = useLocation();
  const isChannelPage = location.pathname.includes('/channel/');

  /* локальный сброс прогресса (UI) */
  const resetProgressLocally = () => {
    setProgress(0); setTokensChecked(0); setTotalTokens(0);
  };

  /* ─────────────────── BOT ─────────────────── */
  const checkBotStatus = useCallback(async () => {
    if (!channelId || !groupId) return;
    try {
      const { data } = await getBotStatus(platform, {
        channel_id: channelId,
        group_id:   groupId,
      });
      setBotRunning(!!data.is_running);
    } catch (e) {
      console.error(e); setBotRunning(false);
    }
  }, [platform, channelId, groupId]);

  const startBot = useCallback(
    async (msgListId) => {
      if (!channelId || !groupId) {
        toastr.error('Не заданы channel_id или group_id', 'Ошибка');
        return;
      }
      try {
        await apiStartBot(platform, {
          channel_id:     channelId,
          group_id:       groupId,
          interval:       intervalValue,
          account_order:  accountMode,
          ...(msgListId && { message_list_id: msgListId }),
        });
        toastr.success('Бот запущен'); setBotRunning(true);
      } catch (e) {
        console.error(e); toastr.error(e.response?.data?.error || 'Ошибка запуска');
      }
    },
    [platform, channelId, groupId, intervalValue, accountMode],
  );

  const stopBot = useCallback(async () => {
    if (!channelId || !groupId) return;
    try {
      await apiStopBot(platform, { channel_id: channelId, group_id: groupId });
      toastr.success('Бот остановлен'); setBotRunning(false);
    } catch (e) {
      console.error(e); toastr.error('Ошибка остановки');
    }
  }, [platform, channelId, groupId]);

  /* ─────────────────── CHAT ─────────────────── */
  const checkChatStatus = useCallback(async () => {
    if (!channelId || !groupId) return;
    try {
      const { data } = await getChatStatus(platform, {
        channel_id: channelId,
        group_id:   groupId,
      });
      setChatRunning(!!data.success);
    } catch (e) {
      console.error(e); setChatRunning(false);
    }
  }, [platform, channelId, groupId]);

  const toggleChat = useCallback(async () => {
    if (!channelId || !groupId) {
      toastr.error('Не заданы channelId или groupId'); return;
    }
    const action = chatRunning ? stopChat : startChat;
    try {
      await action(platform, { channel_id: channelId, group_id: groupId });
      setChatRunning(!chatRunning);
      if (chatRunning) resetProgressLocally();
      toastr.success(chatRunning ? 'Чат остановлен' : 'Чат запущен');
    } catch (e) {
      console.error(e);
      toastr.error('Ошибка работы с чатом');
    }
  }, [platform, channelId, groupId, chatRunning]);

  /* ───────────────── MESSAGE-LISTS ───────────────── */
  const fetchMessageLists = useCallback(async () => {
    if (!channelId) return;
    try {
      const { data } = await getMsgLists(platform, channelId);
      setMessageLists(data);
    } catch (e) {
      console.error(e); toastr.error('Не удалось загрузить списки сообщений');
    }
  }, [platform, channelId]);

  const addMessageList = useCallback(
    async (name, messages, interval) => {
      if (!channelId) return;
      try {
        const body = { name, messages: messages.join('\n'), interval };
        const { data } = await addMsgList(platform, channelId, body);
        toastr.success('Список добавлен');
        fetchMessageLists();
        return data;
      } catch (e) {
        console.error(e); toastr.error('Ошибка добавления списка');
      }
    },
    [platform, channelId, fetchMessageLists],
  );

  const deleteMessageList = useCallback(
    async (id) => {
      try {
        await deleteMsgList(platform, id);
        toastr.success('Список удалён');
        fetchMessageLists();
      } catch (e) {
        console.error(e); toastr.error('Ошибка удаления списка');
      }
    },
    [platform, fetchMessageLists],
  );

  /* ─────────────────── реакция на смену URL ─────────────────── */
  useEffect(() => {
    if (!isChannelPage) {
      setChannelId(null); setGroupId(null);
      setChatRunning(false); setBotRunning(false);
      localStorage.removeItem('channel_id');
      localStorage.removeItem('selectedGroupId');
      return;
    }
    if (channelId && groupId) { checkChatStatus(); checkBotStatus(); }
  }, [isChannelPage, channelId, groupId, checkChatStatus, checkBotStatus]);

  /* ─────────────────── PROVIDER ─────────────────── */
  return (
    <ChannelContext.Provider
      value={{
        /* id */
        groupId, setGroupId,
        channelId, setChannelId,

        /* состояния */
        chatRunning, toggleChat,
        botRunning,  startBot, stopBot,

        /* прогресс */
        tokensChecked, setTokensChecked,
        totalTokens,  setTotalTokens,
        progress,     setProgress,
        resetProgressLocally,

        /* fallback */
        fallbackActive, setFallbackActive,
        fallbackTotal,  setFallbackTotal,

        /* настройки */
        intervalValue, setIntervalValue,
        accountMode,   setAccountMode,

        /* message lists */
        messageLists,
        fetchMessageLists,
        addMessageList,
        deleteMessageList,
      }}
    >
      {children}
    </ChannelContext.Provider>
  );
};
